import React, { useState, useEffect, useCallback } from 'react';
import {
  Typography, Box, Button, TextField,
  Slider, RadioGroup, Radio, Checkbox, FormControlLabel, Switch, FormGroup,
  CircularProgress, Chip,
} from '@mui/material';
import StarIcon from '@mui/icons-material/Star';
import SendIcon from '@mui/icons-material/Send';
import SearchIcon from '@mui/icons-material/Search';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import EditIcon from '@mui/icons-material/Edit';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import BaseDialog from './BaseDialog';
import GameSelectDialog, { GameSelectResult } from './GameSelectDialog';
import StyledTextField from '../common/StyledTextField';
import { useUser } from '../../contexts/UserContext';
import {
  HubAttribute, HubSubmission,
  hubSubmitData, hubGetMyGameSubmissions, hubGetGameAttributes,
  hubGetMyGameConfirmations, hubConfirmValue,
} from '../../services/hubApi';

export const formatAttrValue = (v: any, attr: HubAttribute): string => {
  if (v === null || v === undefined) return '-';
  const config = attr.config || {};
  switch (attr.attribute_type) {
    case 'boolean': return v ? 'はい' : 'いいえ';
    case 'enum': {
      const options: { key: string; label: string }[] = config.options || [];
      if (Array.isArray(v)) return v.map(k => options.find(o => o.key === k)?.label || k).join(', ');
      return options.find(o => o.key === v)?.label || String(v);
    }
    case 'scale': {
      const labels: Record<string, string> = config.labels || {};
      return labels[String(v)] ? `${v} (${labels[String(v)]})` : String(v);
    }
    default: return String(v);
  }
};

export interface HubSubmitDialogProps {
  open: boolean;
  onClose: () => void;
  attribute: HubAttribute | null;
  onSubmitted: () => void;
  /** Pre-selected game — when set, game selector is hidden */
  fixedGame?: { game_id: number; name: string; japanese_name?: string | null; cover?: string } | null;
}

const HubSubmitDialog: React.FC<HubSubmitDialogProps> = ({ open, onClose, attribute, onSubmitted, fixedGame }) => {
  const { refreshUser } = useUser();
  const [selectedGame, setSelectedGame] = useState<GameSelectResult | null>(null);
  const [gameSelectOpen, setGameSelectOpen] = useState(false);
  const [value, setValue] = useState<any>(null);
  const [note, setNote] = useState('');
  const [reason, setReason] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [existingSub, setExistingSub] = useState<HubSubmission | null>(null);

  const [canonicalValue, setCanonicalValue] = useState<any>(undefined);
  const [canonicalNote, setCanonicalNote] = useState('');
  const [confirmationCount, setConfirmationCount] = useState(0);
  const [confirmedAttrIds, setConfirmedAttrIds] = useState<number[]>([]);
  const [editMode, setEditMode] = useState(false);
  const [loadingFactual, setLoadingFactual] = useState(false);
  const [pendingBlocked, setPendingBlocked] = useState(false);

  const resetValue = useCallback((attr: HubAttribute) => {
    const config = attr.config || {};
    if (attr.attribute_type === 'numeric') setValue(config.input_type === 'number' ? '' : (config.min ?? 1));
    else if (attr.attribute_type === 'boolean') setValue(false);
    else if (attr.attribute_type === 'enum') setValue(config.multiple ? [] : (config.options?.[0]?.key || ''));
    else if (attr.attribute_type === 'scale') setValue(config.min ?? 1);
  }, []);

  // Load game data (submissions, factual values)
  const loadGameData = useCallback(async (gameId: number) => {
    if (!attribute) return;
    setExistingSub(null);
    setCanonicalValue(undefined);
    setCanonicalNote('');
    setConfirmationCount(0);
    setEditMode(false);
    setPendingBlocked(false);

    try {
      const { submissions: subs, pending_attribute_ids } = await hubGetMyGameSubmissions(gameId);
      const found = subs.find(s => s.attribute_id === attribute.id);
      if (found) {
        setExistingSub(found);
        setValue(found.value?.v);
        setNote(found.note || '');
      } else {
        resetValue(attribute);
        setNote('');
      }
      if (pending_attribute_ids.includes(attribute.id)) {
        setPendingBlocked(true);
      }
    } catch { /* ignore */ }

    if (attribute.data_mode === 'factual') {
      setLoadingFactual(true);
      try {
        const [aggValues, confs] = await Promise.all([
          hubGetGameAttributes(gameId),
          hubGetMyGameConfirmations(gameId),
        ]);
        const agg = aggValues.find(a => a.attribute_id === attribute.id);
        if (agg) {
          setCanonicalValue(agg.aggregated_value?.result);
          setCanonicalNote(agg.aggregated_value?.canonical_note || '');
          setConfirmationCount(agg.aggregated_value?.confirmation_count ?? 0);
        }
        setConfirmedAttrIds(confs);
      } catch { /* ignore */ }
      setLoadingFactual(false);
    }
  }, [attribute, resetValue]);

  useEffect(() => {
    if (!attribute || !open) return;
    setSelectedGame(null);
    setExistingSub(null);
    setCanonicalValue(undefined);
    setCanonicalNote('');
    setConfirmationCount(0);
    setConfirmedAttrIds([]);
    setEditMode(false);
    setPendingBlocked(false);
    resetValue(attribute);
    setNote('');
    setReason('');
    setError('');

    // If fixedGame is provided, auto-select and load data
    if (fixedGame) {
      const gameResult: GameSelectResult = {
        game_id: fixedGame.game_id,
        name: fixedGame.name,
        japanese_name: fixedGame.japanese_name || null,
        cover: fixedGame.cover || null,
      };
      setSelectedGame(gameResult);
      loadGameData(fixedGame.game_id);
    }
  }, [attribute, open, resetValue, fixedGame, loadGameData]);

  const handleGameSelect = useCallback(async (game: GameSelectResult) => {
    setSelectedGame(game);
    setGameSelectOpen(false);
    await loadGameData(game.game_id);
  }, [loadGameData]);

  if (!attribute) return null;

  const config = attribute.config || {};
  const isFactual = attribute.data_mode === 'factual';
  const hasCanonical = canonicalValue !== undefined && canonicalValue !== null;
  const hasSubmitted = !!existingSub;
  const hasConfirmed = confirmedAttrIds.includes(attribute.id);
  const showForm = !isFactual || !hasCanonical || editMode;
  const activeGame = selectedGame;

  const handleSubmit = async () => {
    if (!activeGame) { setError('ゲームを選択してください'); return; }
    setSubmitting(true);
    setError('');
    try {
      const result = await hubSubmitData({
        attribute_id: attribute.id,
        game_id: activeGame.game_id,
        value: { v: value },
        note: note || undefined,
        reason: reason || undefined,
      });
      // 自動承認でポイント付与された場合、UserContextを更新
      if (result.status === 1 && result.points_awarded > 0) {
        refreshUser();
      }
      onSubmitted();
      onClose();
    } catch (e: any) {
      setError(e.response?.data?.error || '送信に失敗しました');
    }
    setSubmitting(false);
  };

  const handleConfirm = async () => {
    if (!activeGame) return;
    setSubmitting(true);
    setError('');
    try {
      await hubConfirmValue({ attribute_id: attribute.id, game_id: activeGame.game_id });
      onSubmitted();
      onClose();
    } catch (e: any) {
      setError(e.response?.data?.error || '確認に失敗しました');
    }
    setSubmitting(false);
  };

  const handleEnterEditMode = () => {
    if (existingSub && existingSub.status === 0) {
      setValue(existingSub.value?.v);
      setNote(existingSub.note || '');
    } else {
      if (hasCanonical) setValue(canonicalValue);
      setNote(canonicalNote);
    }
    setReason('');
    setEditMode(true);
  };

  const renderInput = () => {
    switch (attribute.attribute_type) {
      case 'numeric': {
        const min = config.min ?? 1;
        const max = config.max ?? 10;
        const step = config.step ?? 1;
        if (config.input_type === 'number') {
          return (
            <TextField
              type="number"
              label={`値 (${min} ~ ${max})`}
              value={value ?? ''}
              onChange={e => {
                const n = Number(e.target.value);
                setValue(isNaN(n) ? '' : n);
              }}
              size="small"
              fullWidth
              inputProps={{ min, max }}
            />
          );
        }
        return (
          <Box sx={{ px: 1 }}>
            <Typography variant="caption" color="text.secondary">
              {min} ~ {max} の範囲で入力
            </Typography>
            <Slider
              value={value ?? min}
              onChange={(_, v) => setValue(v)}
              min={min}
              max={max}
              step={step}
              marks
              valueLabelDisplay="on"
              size="small"
              sx={{ mt: 3 }}
            />
          </Box>
        );
      }
      case 'boolean':
        return (
          <FormControlLabel
            control={<Switch checked={!!value} onChange={e => setValue(e.target.checked)} size="small" />}
            label={<Typography variant="caption">{value ? 'はい' : 'いいえ'}</Typography>}
          />
        );
      case 'enum': {
        const options = config.options || [];
        if (config.multiple) {
          const selected: string[] = Array.isArray(value) ? value : [];
          const toggle = (key: string) => {
            setValue(selected.includes(key) ? selected.filter(k => k !== key) : [...selected, key]);
          };
          return (
            <FormGroup>
              {options.map((opt: { key: string; label: string }) => (
                <FormControlLabel
                  key={opt.key}
                  control={<Checkbox size="small" checked={selected.includes(opt.key)} onChange={() => toggle(opt.key)} />}
                  label={<Typography variant="caption">{opt.label}</Typography>}
                  sx={{ my: -0.5 }}
                />
              ))}
            </FormGroup>
          );
        }
        return (
          <RadioGroup value={value || ''} onChange={e => setValue(e.target.value)}>
            {options.map((opt: { key: string; label: string }) => (
              <FormControlLabel
                key={opt.key}
                value={opt.key}
                control={<Radio size="small" />}
                label={<Typography variant="caption">{opt.label}</Typography>}
                sx={{ my: -0.5 }}
              />
            ))}
          </RadioGroup>
        );
      }
      case 'scale': {
        const min = config.min ?? 1;
        const max = config.max ?? 5;
        const labels = config.labels || {};
        return (
          <Box sx={{ px: 1 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
              <Typography variant="caption" color="text.secondary">{labels[String(min)] || min}</Typography>
              <Typography variant="caption" color="text.secondary">{labels[String(max)] || max}</Typography>
            </Box>
            <Slider
              value={value ?? min}
              onChange={(_, v) => setValue(v)}
              min={min}
              max={max}
              step={1}
              marks={Array.from({ length: max - min + 1 }, (_, i) => ({ value: min + i, label: String(min + i) }))}
              valueLabelDisplay="auto"
              size="small"
            />
          </Box>
        );
      }
      default:
        return <TextField label="値" value={value || ''} onChange={e => setValue(e.target.value)} size="small" fullWidth />;
    }
  };

  const renderActions = () => {
    if (isFactual && hasCanonical && !editMode) {
      return (
        <>
          <Button onClick={onClose} size="small">閉じる</Button>
          {!hasSubmitted && !hasConfirmed && (
            <Button
              onClick={handleConfirm}
              variant="contained"
              color="success"
              size="small"
              disabled={submitting || !activeGame || pendingBlocked}
              startIcon={submitting ? <CircularProgress size={14} /> : <ThumbUpIcon sx={{ fontSize: 16 }} />}
            >
              データの正確性に同意
            </Button>
          )}
        </>
      );
    }
    return (
      <>
        <Button onClick={isFactual && hasCanonical ? () => setEditMode(false) : onClose} size="small">
          {isFactual && hasCanonical ? '戻る' : 'キャンセル'}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          size="small"
          disabled={submitting || !activeGame || pendingBlocked}
          startIcon={submitting ? <CircularProgress size={14} /> : <SendIcon sx={{ fontSize: 16 }} />}
        >
          {existingSub ? '更新' : '送信'}
        </Button>
      </>
    );
  };

  return (
    <>
      <BaseDialog
        open={open}
        onClose={onClose}
        title={attribute.name}
        maxWidth="xs"
        actions={renderActions()}
      >
        {attribute.description && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.5 }}>
            {attribute.description}
          </Typography>
        )}

        {isFactual && (
          <Chip label="ファクト" size="small" color="info" variant="outlined" sx={{ mb: 1.5 }} />
        )}

        {/* Game selector — hidden when fixedGame is provided */}
        {!fixedGame && (
          <>
            <Typography variant="caption" fontWeight="bold" sx={{ mb: 0.5, display: 'block' }}>対象ゲーム</Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
              <Button
                variant="outlined"
                startIcon={<SearchIcon sx={{ fontSize: 16 }} />}
                onClick={() => setGameSelectOpen(true)}
                size="small"
              >
                {selectedGame ? '変更' : 'ゲームを検索'}
              </Button>
              {selectedGame && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  {selectedGame.cover && (
                    <Box
                      component="img"
                      src={selectedGame.cover}
                      alt=""
                      sx={{ width: 24, height: 32, borderRadius: 0.5, objectFit: 'cover' }}
                    />
                  )}
                  <Typography variant="caption" fontWeight="bold">
                    {selectedGame.japanese_name || selectedGame.name}
                  </Typography>
                </Box>
              )}
            </Box>
          </>
        )}

        {/* Fixed game display */}
        {fixedGame && activeGame && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2, p: 1, bgcolor: 'rgba(255,255,255,0.03)', borderRadius: 1 }}>
            {activeGame.cover && (
              <Box
                component="img"
                src={activeGame.cover}
                alt=""
                sx={{ width: 24, height: 32, borderRadius: 0.5, objectFit: 'cover' }}
              />
            )}
            <Typography variant="caption" fontWeight="bold">
              {activeGame.japanese_name || activeGame.name}
            </Typography>
          </Box>
        )}

        {loadingFactual && <CircularProgress size={20} sx={{ display: 'block', mx: 'auto', my: 2 }} />}

        {isFactual && activeGame && !loadingFactual && hasCanonical && !editMode && (
          <Box sx={{ mb: 1.5 }}>
            <Box sx={{ p: 1.5, bgcolor: 'action.hover', borderRadius: 1, mb: 1.5 }}>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 0.5 }}>
                現在の値
              </Typography>
              <Typography variant="body1" fontWeight="bold">
                {formatAttrValue(canonicalValue, attribute)}
              </Typography>
              {confirmationCount > 0 && (
                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                  {confirmationCount} 人が確認済み
                </Typography>
              )}
            </Box>

            {hasConfirmed && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, p: 0.75, bgcolor: 'success.main', borderRadius: 1, opacity: 0.15 }}>
                <CheckCircleIcon sx={{ fontSize: 16 }} />
                <Typography variant="caption" fontWeight="bold">確認済み</Typography>
              </Box>
            )}
            {hasSubmitted && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1, p: 0.75, bgcolor: 'action.hover', borderRadius: 1 }}>
                <CheckCircleIcon sx={{ fontSize: 16, color: existingSub!.status === 0 ? 'warning.main' : 'success.main' }} />
                <Typography variant="caption" color={existingSub!.status === 0 ? 'warning.main' : 'success.main'} fontWeight="bold">
                  {existingSub!.status === 0 ? '承認待ち' : 'あなたの投稿'}: {formatAttrValue(existingSub!.value?.v, attribute)}
                </Typography>
              </Box>
            )}

            <Button
              size="small"
              startIcon={<EditIcon sx={{ fontSize: 16 }} />}
              onClick={handleEnterEditMode}
              sx={{ mt: 0.5 }}
            >
              値を編集
            </Button>
          </Box>
        )}

        {isFactual && activeGame && !loadingFactual && !hasCanonical && !editMode && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
            まだデータがありません。最初の値を投稿してください。
          </Typography>
        )}

        {activeGame && !loadingFactual && pendingBlocked && (
          <Box sx={{ p: 1.5, bgcolor: 'warning.main', borderRadius: 1, opacity: 0.9 }}>
            <Typography variant="caption" color="warning.contrastText" fontWeight="bold">
              現在審査待ちの投稿があります。審査完了後に投稿できます。
            </Typography>
          </Box>
        )}

        {activeGame && !loadingFactual && showForm && !pendingBlocked && (
          <>
            {!isFactual && existingSub && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1.5, p: 0.75, bgcolor: 'action.hover', borderRadius: 1 }}>
                <CheckCircleIcon sx={{ fontSize: 16, color: existingSub.status === 0 ? 'warning.main' : 'success.main' }} />
                <Typography variant="caption" color={existingSub.status === 0 ? 'warning.main' : 'success.main'} fontWeight="bold">
                  {existingSub.status === 0 ? '承認待ち' : '投稿済み'} (現在の値: {formatAttrValue(existingSub.value?.v, attribute)})
                </Typography>
                {existingSub.is_edit && (
                  <Chip label="編集" size="small" color="warning" variant="outlined" sx={{ ml: 0.5, height: 20, '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' } }} />
                )}
              </Box>
            )}

            {renderInput()}

            {attribute.note_mode !== 'disabled' && (
              <StyledTextField
                label={`補足情報${attribute.note_mode === 'required' ? ' (必須)' : ' (任意)'}`}
                value={note}
                onChange={setNote}
                size="small"
                minRows={2}
                maxRows={4}
                sx={{ mt: 1.5 }}
              />
            )}

            {(isFactual && editMode) && (
              <StyledTextField
                label="変更理由 (必須)"
                value={reason}
                onChange={setReason}
                size="small"
                minRows={2}
                maxRows={4}
                sx={{ mt: 1.5 }}
              />
            )}
          </>
        )}

        {error && <Typography color="error" variant="caption" sx={{ mt: 0.5, display: 'block' }}>{error}</Typography>}

        {attribute.points_reward > 0 && showForm && !existingSub && (
          <Box sx={{ mt: 1.5, display: 'flex', alignItems: 'center', gap: 0.5 }}>
            <StarIcon sx={{ fontSize: 14, color: 'primary.main' }} />
            <Typography variant="caption" color="primary">
              {attribute.points_reward}pt 獲得
            </Typography>
          </Box>
        )}
      </BaseDialog>
      {!fixedGame && (
        <GameSelectDialog
          open={gameSelectOpen}
          onClose={() => setGameSelectOpen(false)}
          onSelect={handleGameSelect}
          title="データを投稿するゲームを選択"
          showAllSearchOption
        />
      )}
    </>
  );
};

export default HubSubmitDialog;
